<template>
  <div class="minimal-profile">
    <!-- Navbar -->
    <nav class="navbar" :class="{ 'scrolled': isScrolled }">
      <div class="nav-content">
        <div class="nav-brand text-gradient-inverted">GHdev</div>
        <div class="nav-links hidden-mobile">
          <a href="#about">Sobre mi</a>
          <a href="#projects">Proyectos</a>
          <a href="#skills">Skills</a>
          <a href="#contact">Contacto</a>
        </div>
        <a href="#contact" class="text-link hidden-mobile">Hablemos ↗</a>
      </div>
    </nav>

    <main class="main-content">
      <!-- Hero Section -->
      <section id="hero" class="section hero">
        <div class="container hero-container fade-up">
          <h1 class="hero-title">
            Arquitecto de software y <br />
            <span class="text-gradient">soluciones tecnológicas</span>
          </h1>
          <p class="hero-desc">
            Diseño sistemas complejos con propósito: resolver desafíos reales del negocio. Conecto arquitectura de software, inteligencia artificial y estrategia para crear soluciones robustas, escalables y con impacto.
          </p>
          <div class="hero-actions">
            <a href="#projects" class="text-link text-link-primary">Ver Proyectos ↓</a>
            <a href="#contact" class="text-link">Contactar ↗</a>
          </div>
        </div>
      </section>

      <!-- About Section -->
      <section id="about" class="section">
        <div class="container fade-up">
          <h2 class="section-title">Sobre mi</h2>
          <div class="about-grid">
            <div class="about-text">
              <p>Soy arquitecto de software senior con más de 10 años de experiencia diseñando e implementando soluciones robustas en múltiples sectores.</p>
              <p>A lo largo de mi carrera he comprendido que la tecnología por sí sola no basta: debe responder a problemas concretos, con impacto real en el negocio.</p>
              <blockquote class="about-quote">
                "Desarrollo soluciones del presente con tecnologías del futuro. Transformo desafíos técnicos en oportunidades de negocio."
              </blockquote>
            </div>
            <div class="about-cards">
              <div class="feature-card">
                <h3>Arquitectura avanzada</h3>
                <p>Sistemas distribuidos, resilientes y escalables (Clean Architecture, DDD, microservicios).</p>
              </div>
              <div class="feature-card">
                <h3>Inteligencia Artificial</h3>
                <p>Visión computacional, LLMs y modelos de aprendizaje profundo para el mundo real.</p>
              </div>
              <div class="feature-card">
                <h3>Consultoría estratégica</h3>
                <p>Auditorías técnicas, análisis de procesos e identificación de cuellos de botella.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Projects Section -->
      <section id="projects" class="section section-alt">
        <div class="container fade-up">
          <h2 class="section-title">Proyectos destacados</h2>
          <div class="projects-grid">
            <article class="project-card" v-for="(project, index) in projects" :key="index">
              <div class="project-meta">
                <span class="project-number">{{ String(index + 1).padStart(2, '0') }}</span>
                <span class="project-role">{{ project.role }}</span>
              </div>
              <h3 class="project-title">{{ project.title }}</h3>
              <p class="project-desc">{{ project.desc }}</p>
              <div class="tech-tags">
                <span class="tag" v-for="tag in project.tags" :key="tag">{{ tag }}</span>
              </div>
            </article>
          </div>
        </div>
      </section>

      <!-- Skills Section -->
      <section id="skills" class="section">
        <div class="container fade-up">
          <h2 class="section-title">Habilidades técnicas</h2>
          <div class="skills-grid">
            <div class="skill-group" v-for="(skillGroup, index) in skills" :key="index">
              <h3 class="skill-group-title">{{ skillGroup.title }}</h3>
              <ul class="skill-list">
                <li v-for="(item, i) in skillGroup.items" :key="i">{{ item }}</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <!-- Contact Section -->
      <section id="contact" class="section section-alt">
        <div class="container fade-up text-center contact-container">
          <h2 class="section-title">Contacto</h2>
          <p class="contact-desc">
            Estoy abierto a colaborar en proyectos que requieran visión, experiencia y profundidad técnica. <br/>¿Tienes una idea, sistema o desafío en marcha?
          </p>
          <div class="contact-actions">
            <a href="mailto:hola@gonzalohonorato.cl" class="email-link">hola@gonzalohonorato.cl</a>
            <span class="location-tag">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
              Santiago, Chile
            </span>
          </div>
        </div>
      </section>
    </main>

    <footer class="footer">
      <div class="container footer-container">
        <p>© {{ new Date().getFullYear() }} Gonzalo Honorato.</p>
        <p class="footer-design">Designed & Built with elegance.</p>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';

const isScrolled = ref(false);

const handleScroll = () => {
  isScrolled.value = window.scrollY > 50;
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
  
  // Setup intersection observer for fade-up animations
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px'
  });

  document.querySelectorAll('.fade-up').forEach((el) => {
    observer.observe(el);
  });
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

const projects = [
  {
    title: 'Sistema de pesaje inteligente para camiones',
    role: 'Arquitecto de software y líder técnico',
    desc: 'Sistema modular para básculas de camiones: conexión con indicadores de peso, detección de ejes, control de tránsito, ticketing automático, OCR de patentes, integración de hardware y software con lógica de negocio compleja.',
    tags: ['VB.NET', 'SQL Server', 'TCP', 'Electron', 'Vue']
  },
  {
    title: 'Framework de auditoría visual con IA local',
    role: 'CTO externo y desarrollador principal',
    desc: 'Sistema para captura, OCR local con LLM, persistencia de auditorías, y visualización. Incluye selección de ROI por cámara, logging detallado y endpoints REST.',
    tags: ['Flask', 'SQLite', 'Ollama Vision', 'OpenCV', 'API REST']
  },
  {
    title: 'Core distribuido para control de dispositivos',
    role: 'Arquitecto principal',
    desc: 'Arquitectura para gestión de dispositivos sensorizados con paneles modulares, normalización de datos, reglas de automatización, y microservicios de extracción/inyección por protocolo.',
    tags: ['Electron', 'Vue 3', 'Node.js', 'Go', 'TimescaleDB']
  },
  {
    title: 'Monitorización meteorológica distribuida',
    role: 'Arquitecto de software y líder técnico',
    desc: 'Sistema completo de monitorización para estaciones meteorológicas con múltiples sensores, comunicaciones heterogéneas y procesamiento en tiempo real.',
    tags: ['Go', 'Kafka', 'Kubernetes', 'Vue 3', 'TimescaleDB']
  },
  {
    title: 'Ecosistema de envíos multi-canal',
    role: 'Líder técnico y arquitecto principal',
    desc: 'Plataforma integral de gestión logística con cientos de integraciones: marketplaces, operadores logísticos, gestión de envíos, pedidos, tickets, fulfillment.',
    tags: ['Node.js', 'React', 'PostgreSQL', 'Kubernetes', 'Event Sourcing']
  },
  {
    title: 'Sistema integral para clínicas veterinarias',
    role: 'Desarrollador principal y arquitecto',
    desc: 'Sistema completo de gestión clínica veterinaria con historia médica digital, agenda inteligente, facturación, inventario, firmas digitales y sincronización offline.',
    tags: ['Vue 3', 'Go', 'TypeScript', 'PWA', 'PostgreSQL']
  }
];

const skills = [
  {
    title: 'Arquitectura y Backend',
    items: [
      'Diseño de sistemas escalables y resilientes',
      'Clean Architecture, DDD, Microservicios',
      'C#, VB.NET, Go, Python, Node.js',
      'SQL Server, PostgreSQL, TimescaleDB, NoSql',
      'Docker, Kubernetes, CI/CD'
    ]
  },
  {
    title: 'Frontend',
    items: [
      'Vue 2 / Vue 3, React, Nuxt, TypeScript',
      'Electron, SPA, PWA',
      'HTML, CSS, JavaScript vanilla',
      'Vuetify, Pinia, Tailwind'
    ]
  },
  {
    title: 'Inteligencia Artificial',
    items: [
      'Computer Vision con OpenCV y YOLOv8',
      'Procesamiento de imágenes, ROI dinámico',
      'LLMs offline y locales',
      'Sistemas de control basados en workflows'
    ]
  },
  {
    title: 'Sistemas de Agentes IA',
    items: [
      'Arquitectura de agentes distribuidos',
      'Orquestación de workflows complejos con IA',
      'Coordinación entre agentes autónomos',
      'Escalabilidad horizontal de agentes'
    ]
  },
  {
    title: 'Eventos y Mensajería',
    items: [
      'Apache Kafka, RabbitMQ, Redis Pub/Sub',
      'Event Sourcing y CQRS',
      'Arquitecturas basadas en eventos'
    ]
  },
  {
    title: 'Protocolos y Comunicación',
    items: [
      'TCP, Serial (RS232/RS485), WebSocket, MQTT',
      'Comunicación entre hardware/software',
      'Manejo de drivers físicos y virtuales'
    ]
  }
];
</script>

<style scoped>
/* Base & Typography */
.minimal-profile {
  background: var(--bg-primary);
  color: var(--text-primary);
  line-height: 1.6;
}

.container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.section {
  padding: 6rem 0;
}

.section-alt {
  background: transparent;
}

.section-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 3rem;
  letter-spacing: -0.02em;
}

.text-gradient {
  background: linear-gradient(135deg, var(--text-primary) 0%, var(--text-muted) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.text-gradient-inverted {
  background: linear-gradient(135deg, var(--text-muted) 0%, var(--text-primary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Animations */
.fade-up {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.8s ease, transform 0.8s ease;
}

.fade-up.is-visible {
  opacity: 1;
  transform: translateY(0);
}

/* Navbar */
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  padding: 1.5rem 0;
  transition: all 0.3s ease;
}

.navbar.scrolled {
  padding: 1rem 0;
  background: var(--bg-glass);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border-dim);
}

.nav-content {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.nav-brand {
  font-weight: 700;
  font-size: 1.25rem;
  letter-spacing: -0.02em;
}

.nav-links {
  display: flex;
  gap: 2rem;
}

.nav-links a {
  text-decoration: none;
  color: var(--text-secondary);
  font-size: 0.9rem;
  font-weight: 500;
  transition: color 0.2s ease;
}

.nav-links a:hover {
  color: var(--text-primary);
}

/* Links & Actions */
.text-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-weight: 500;
  font-size: 0.95rem;
  color: var(--text-secondary);
  text-decoration: none;
  position: relative;
  transition: color 0.3s ease;
  cursor: pointer;
}

.text-link:hover {
  color: var(--text-primary);
}

.text-link::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  height: 1px;
  background-color: var(--text-primary);
  transform: scaleX(0);
  transform-origin: right;
  transition: transform 0.3s ease;
}

.text-link:hover::after {
  transform: scaleX(1);
  transform-origin: left;
}

.text-link-primary {
  color: var(--text-primary);
  font-weight: 600;
}

.email-link {
  font-size: clamp(1.2rem, 3vw, 1.5rem);
  font-weight: 600;
  color: var(--text-primary);
  text-decoration: none;
  position: relative;
  display: inline-block;
  padding-bottom: 0.25rem;
}

.email-link::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: var(--text-primary);
  transform: scaleX(0);
  transform-origin: right;
  transition: transform 0.4s ease;
}

.email-link:hover::after {
  transform: scaleX(1);
  transform-origin: left;
}
/* Hero */
.hero {
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding-top: 5rem; /* Account for navbar */
}

.hero-container {
  max-width: 800px;
  margin: 0; /* Left align hero text */
}

.hero-title {
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.03em;
  margin-bottom: 1.5rem;
}

.hero-desc {
  font-size: clamp(1.1rem, 2vw, 1.25rem);
  color: var(--text-secondary);
  max-width: 650px;
  margin-bottom: 2.5rem;
  line-height: 1.8;
}

.hero-actions {
  display: flex;
  gap: 1rem;
}

/* About */
.about-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
}

.about-text p {
  font-size: 1.1rem;
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
  line-height: 1.8;
}

.about-quote {
  font-size: 1.25rem;
  font-style: italic;
  color: var(--text-primary);
  padding-left: 1.5rem;
  border-left: 3px solid var(--border-active);
  margin-top: 2rem;
}

.about-cards {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.feature-card {
  padding: 1.5rem 0;
  border-top: 1px solid var(--border-dim);
}

.feature-card h3 {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: var(--text-primary);
}

.feature-card p {
  font-size: 0.95rem;
  color: var(--text-secondary);
  margin: 0;
}

/* Projects */
.projects-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2rem;
}

.project-card {
  display: flex;
  flex-direction: column;
  padding: 2rem 0;
  border-top: 1px solid var(--border-dim);
}

.project-meta {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.project-number {
  font-family: var(--font-mono);
  color: var(--text-muted);
  font-size: 0.9rem;
}

.project-role {
  font-size: 0.85rem;
  color: var(--text-secondary);
  font-weight: 500;
}

.project-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.project-desc {
  font-size: 0.95rem;
  color: var(--text-secondary);
  flex-grow: 1;
  margin-bottom: 1.5rem;
}

.tech-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tag {
  font-family: var(--font-mono);
  font-size: 0.75rem;
  padding: 0.25rem 0.6rem;
  border: 1px solid var(--border-dim);
  border-radius: 4px;
  color: var(--text-muted);
}

/* Skills */
.skills-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 3rem 2rem;
}

.skill-group-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1.25rem;
}

.skill-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.skill-list li {
  font-size: 0.95rem;
  color: var(--text-secondary);
  margin-bottom: 0.75rem;
  position: relative;
  padding-left: 1.25rem;
}

.skill-list li::before {
  content: '→';
  position: absolute;
  left: 0;
  color: var(--text-muted);
  font-family: var(--font-mono);
}

/* Contact */
.text-center {
  text-align: center;
}

.contact-container {
  max-width: 600px;
  margin: 0 auto;
}

.contact-desc {
  font-size: 1.15rem;
  color: var(--text-secondary);
  margin-bottom: 3rem;
  line-height: 1.8;
}

.contact-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.location-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-muted);
  font-size: 0.95rem;
}

/* Footer */
.footer {
  padding: 2rem 0;
  border-top: 1px solid var(--border-dim);
  margin-top: auto;
}

.footer-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--text-muted);
  font-size: 0.9rem;
}

/* Responsive */
@media (max-width: 768px) {
  .hidden-mobile {
    display: none;
  }
  
  .about-grid {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
  
  .hero-title {
    font-size: 2.5rem;
  }
  
  .hero-actions {
    flex-direction: column;
  }
  
  .text-link {
    font-size: 1.05rem;
  }

  .footer-container {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
}
</style>
